import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Heart, Menu, X, MapPin, CalendarDays } from "lucide-react";
import { useLang, LANGS, LANG_LABELS } from "@/i18n";
import { SECTIONS } from "@/config";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

export function Logo({ dark = false }) {
  return (
    <Link to="/" data-testid="logo-link" className="flex items-center select-none">
      <img src="/logo-lungo.png" alt="Like Follonica" className="h-9 w-auto" />
    </Link>
  );
}

export function LangSwitcher({ compact = false }) {
  const { lang, setLang } = useLang();
  return (
    <div data-testid="lang-switcher" className="flex items-center rounded-full border border-black/10 bg-white/60 overflow-hidden">
      {LANGS.map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1 text-xs font-bold tracking-wider transition-colors ${
            lang === l ? "bg-mare text-calce" : "text-grafite/70 hover:text-mare"
          }`}
        >
          {LANG_LABELS[l]}
        </button>
      ))}
    </div>
  );
}

export default function Navbar() {
  const { t } = useLang();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const main = SECTIONS.filter((s) => ["accommodation", "restaurant", "activity", "event"].includes(s.type));
  const more = SECTIONS.filter((s) => !["accommodation", "restaurant", "activity", "event"].includes(s.type));
  const linkCls = (path) =>
    `font-semibold text-sm transition-colors hover:text-terracotta ${location.pathname === path ? "text-terracotta" : "text-grafite"}`;

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/70 border-b border-black/5" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <Logo />
        <nav className="hidden lg:flex items-center gap-6">
          {main.map((s) => (
            <Link key={s.type} to={s.path} data-testid={`nav-${s.type}`} className={linkCls(s.path)}>
              {t(s.navKey)}
            </Link>
          ))}
          <Link to="/mappa" data-testid="nav-map" className={linkCls("/mappa")}>
            <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" />{t("nav.map")}</span>
          </Link>
        </nav>
        <div className="flex items-center gap-2">
          <LangSwitcher />
          <Link
            to="/preferiti"
            data-testid="nav-favorites"
            className="hidden sm:flex w-9 h-9 rounded-full bg-sabbia items-center justify-center hover:bg-terracotta hover:text-white text-terracotta transition-colors"
            aria-label={t("nav.favorites")}
          >
            <Heart className="w-4 h-4" />
          </Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="nav-menu-button" className="lg:hidden w-9 h-9 rounded-full bg-mare text-calce flex items-center justify-center" aria-label="Menu">
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[86vw] sm:w-96 bg-calce overflow-y-auto">
              <div className="flex items-center justify-between mb-8 mt-2">
                <Logo />
                <button data-testid="nav-menu-close" onClick={() => setOpen(false)} aria-label="Chiudi">
                  <X className="w-6 h-6 text-grafite" />
                </button>
              </div>
              <nav className="flex flex-col gap-1">
                {SECTIONS.map((s) => {
                  const Icon = s.icon;
                  return (
                    <Link
                      key={s.type}
                      to={s.path}
                      data-testid={`menu-${s.type}`}
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-2xl font-heading font-semibold text-lg text-grafite hover:bg-sabbia transition-colors"
                    >
                      <span className="w-9 h-9 rounded-full flex items-center justify-center text-white" style={{ background: s.color }}>
                        <Icon className="w-4 h-4" />
                      </span>
                      {t(s.navKey)}
                    </Link>
                  );
                })}
                <Link to="/mappa" data-testid="menu-map" onClick={() => setOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl font-heading font-semibold text-lg text-grafite hover:bg-sabbia transition-colors">
                  <span className="w-9 h-9 rounded-full bg-mare flex items-center justify-center text-white"><MapPin className="w-4 h-4" /></span>
                  {t("nav.map")}
                </Link>
                <div className="mt-4 pt-4 border-t border-black/10 flex flex-col gap-1">
                  <Link to="/preferiti" data-testid="menu-favorites" onClick={() => setOpen(false)} className="px-4 py-2.5 font-semibold text-terracotta">♥ {t("nav.favorites")}</Link>
                  <Link to="/sondaggio" data-testid="menu-survey" onClick={() => setOpen(false)} className="px-4 py-2.5 font-semibold text-mare">{t("nav.survey")}</Link>
                  <Link to="/chi-siamo" data-testid="menu-about" onClick={() => setOpen(false)} className="px-4 py-2.5 font-semibold text-grafite">{t("nav.about")}</Link>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <div className="hidden lg:block border-t border-black/5">
        <div className="max-w-7xl mx-auto px-6 h-9 flex items-center gap-5 overflow-x-auto">
          {more.map((s) => (
            <Link key={s.type} to={s.path} data-testid={`subnav-${s.type}`} className="text-xs font-semibold text-grafite/60 hover:text-mare transition-colors whitespace-nowrap">
              {t(s.navKey)}
            </Link>
          ))}
          <Link to="/sondaggio" className="text-xs font-semibold text-mare hover:text-terracotta transition-colors whitespace-nowrap">{t("nav.survey")}</Link>
          <Link to="/chi-siamo" className="text-xs font-semibold text-grafite/60 hover:text-mare transition-colors whitespace-nowrap">{t("nav.about")}</Link>
          <Link to="/preferiti" className="text-xs font-semibold text-terracotta whitespace-nowrap ml-auto">♥ {t("nav.favorites")}</Link>
        </div>
      </div>
    </header>
  );
}
